import React, { useEffect } from "react";

import { useQuery } from "@tanstack/react-query";
import classNames from "classnames";
import { Controller, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import ReactSelect from "react-select";

import FastAPI from "../../../api/FastAPI";
import { TimeslotRuleMode } from "../../../api/FastAPI.types";
import MediaAPI from "../../../api/MediaAPI";
import { AD_BREAK, MEDIA_FORMATS } from "../../../constants/media";
import {
  clearIndicatorStyles,
  controlStyles,
  dropdownIndicatorStyles,
  groupHeadingStyles,
  indicatorsContainerStyles,
  indicatorSeparatorStyles,
  menuStyles,
  noOptionsMessageStyles,
  optionStyles,
  placeholderStyles,
  selectInputStyles,
  singleValueStyles,
  valueContainerStyles,
} from "../../form/Select";

import type { JSX } from "react";

import type { TimeslotFormValues } from "./TimeslotEdit";
import type {
  TimeslotAdbreakRule,
  TimeslotRandomRule,
  TimeslotRule,
  TimeslotSequentialRule,
} from "../../../api/FastAPI.types";
import type { FASTChannelRoutesParams } from "../../routes/FASTChannelRoutes";

interface SelectStatus {
  isFocused: boolean;
  isSelected: boolean;
}
type SelectClassNames = Record<
  string,
  ({ isFocused, isSelected }: SelectStatus) => string
>;

interface TimeslotRuleItemSelectProps {
  fieldName: string;
  options?: { value: string | number; label: string | number }[];
  isReadOnly?: boolean;
  placeholder?: string;
  defaultValue?: { value: string | number; label: string | number };
  isLoading?: boolean;
  isRequired?: boolean;
  isClearable?: boolean;
  containerClassName?: string;
}

function TimeslotRuleItemSelect({
  fieldName,
  options,
  isReadOnly,
  placeholder,
  defaultValue,
  isLoading,
  isRequired,
  isClearable,
  containerClassName,
}: TimeslotRuleItemSelectProps): JSX.Element {
  const { control, getFieldState } = useFormContext();
  const isInvalid = getFieldState(fieldName)?.invalid;
  const { t } = useTranslation();

  const getSelectClassNames = (): SelectClassNames => ({
    control: ({ isFocused }: SelectStatus) =>
      classNames(
        isInvalid && controlStyles.isError,
        isFocused ? controlStyles.focus : controlStyles.nonFocus,
        controlStyles.base,
        isReadOnly && controlStyles.isReadOnly,
      ),
    input: () => selectInputStyles,
    placeholder: () => placeholderStyles,
    valueContainer: () => valueContainerStyles,
    singleValue: () => singleValueStyles,
    indicatorsContainer: () => indicatorsContainerStyles,
    clearIndicator: () => clearIndicatorStyles,
    indicatorSeparator: () => indicatorSeparatorStyles,
    dropdownIndicator: () => dropdownIndicatorStyles,
    menu: () => menuStyles,
    groupHeading: () => groupHeadingStyles,
    option: ({ isFocused, isSelected }: SelectStatus) =>
      classNames(
        isFocused && optionStyles.focus,
        isSelected && optionStyles.selected,
        optionStyles.base,
      ),
    noOptionsMessage: () => noOptionsMessageStyles,
  });

  const wrapperClassName = classNames("relative w-full", containerClassName);

  return (
    <div className={wrapperClassName}>
      <Controller
        control={control}
        name={fieldName}
        render={({ field }): JSX.Element => (
          <ReactSelect
            menuShouldBlockScroll
            menuShouldScrollIntoView
            unstyled
            classNames={getSelectClassNames()}
            defaultValue={defaultValue}
            isClearable={isClearable}
            isDisabled={isReadOnly}
            isLoading={isLoading}
            menuPortalTarget={document.body}
            menuPosition="fixed"
            options={options}
            loadingMessage={(): string =>
              t("loading", { ns: "editor-content" })
            }
            noOptionsMessage={({ inputValue }): string | false =>
              !inputValue && "no options"
            }
            placeholder={
              placeholder ??
              t("select", {
                ns: "forms",
              })
            }
            styles={{
              control: () => ({}),
            }}
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...field}
            onChange={(selectedOption): void => {
              field.onChange(selectedOption?.value ?? null);
            }}
            value={
              options?.find((option) => option.value === field.value) ??
              defaultValue ??
              null
            }
          />
        )}
        rules={{
          required:
            isRequired &&
            t("requiredField", {
              ns: "editor-errors",
            }),
        }}
      />
    </div>
  );
}

interface TimeslotRuleMediaSelectProps {
  ruleId: string;
}

export function TimeslotRuleMediaSelect({
  ruleId,
}: TimeslotRuleMediaSelectProps): JSX.Element {
  const { channelId } = useParams<FASTChannelRoutesParams>();
  const getFastMediaListQuery = MediaAPI.generateQuery({
    fn: MediaAPI.getFastIndexMediaList,
    params: [
      {
        channelId,
        filter: {
          format: {
            excludes: [MEDIA_FORMATS.ADPOD, AD_BREAK],
          },
        },
        perPage: -1,
      },
    ],
  });

  const { data: mediaList } = useQuery({
    ...getFastMediaListQuery,
  });

  const options =
    mediaList?.data.map((media) => ({
      label: media.label,
      value: media.uuid,
    })) ?? [];

  return (
    <TimeslotRuleItemSelect
      isRequired
      fieldName={`rulesById.${ruleId}.media_uuid`}
      options={options}
    />
  );
}

export function TimeslotRuleSingleCountSelect(): JSX.Element {
  return (
    <TimeslotRuleItemSelect
      isReadOnly
      defaultValue={{ value: "1", label: "1" }}
      fieldName="readOnlyRuleCount"
    />
  );
}

interface TimeslotRuleAdbreakSelectProps {
  ruleId: string;
}

export function TimeslotRuleAdbreakSelect({
  ruleId,
}: TimeslotRuleAdbreakSelectProps): JSX.Element {
  const { channelId } = useParams<{ channelId: string }>();
  const { t } = useTranslation("editor-fast-channel");
  const { watch } =
    useFormContext<
      FormValues<
        TimeslotRandomRule | TimeslotSequentialRule | TimeslotAdbreakRule
      >
    >();

  const formValues = watch(`rulesById.${ruleId}`);
  const elements = formValues?.elements;

  const isRandomMode = formValues?.mode === TimeslotRuleMode.Random;
  const isSequentialMode = formValues?.mode === TimeslotRuleMode.Sequential;
  const isAdbreakMode = formValues?.mode === TimeslotRuleMode.Adbreak;

  const getAdBreaksListQuery = FastAPI.generateQuery({
    fn: FastAPI.getAdBreaksList,
    params: [channelId],
  });

  const { data: adBreaksList, isFetching } = useQuery({
    ...getAdBreaksListQuery,
  });

  const adBreaksOptions =
    adBreaksList?.data.map((playlist) => ({
      label: playlist.name,
      value: playlist.uuid,
    })) ?? [];

  const getReadOnlyPlaceholder = (): string | undefined => {
    if (isRandomMode || isSequentialMode) {
      if (!formValues?.fast_channel_playlist_uuid) {
        return t("selectPlaylistFirst");
      }
      if (elements === 1) {
        return t("selectMoreThanOneElement");
      }
      return undefined;
    }
    return undefined;
  };

  const readOnlyPlaceholder = getReadOnlyPlaceholder();

  const isReadOnly = Boolean(readOnlyPlaceholder);

  return (
    <TimeslotRuleItemSelect
      fieldName={`rulesById.${ruleId}.adbreak_uuid`}
      isClearable={!isAdbreakMode}
      isLoading={isFetching}
      isReadOnly={isReadOnly}
      isRequired={isAdbreakMode}
      options={adBreaksOptions}
      placeholder={readOnlyPlaceholder}
    />
  );
}

interface TimeslotRulePlaylistSelectProps {
  ruleId: string;
}

export function TimeslotRulePlaylistSelect({
  ruleId,
}: TimeslotRulePlaylistSelectProps): JSX.Element {
  const { channelId } = useParams<{ channelId: string }>();

  const getPlaylistListQuery = FastAPI.generateQuery({
    fn: FastAPI.getPlaylistList,
    params: [channelId],
  });

  const { data: playlistList, isFetching } = useQuery({
    ...getPlaylistListQuery,
  });

  const playlistOptions =
    playlistList?.data.map((playlist) => ({
      label: playlist.name,
      value: playlist.uuid,
    })) ?? [];

  return (
    <TimeslotRuleItemSelect
      isRequired
      fieldName={`rulesById.${ruleId}.fast_channel_playlist_uuid`}
      isLoading={isFetching}
      options={playlistOptions}
    />
  );
}

interface FormValues<T extends TimeslotRule> extends Omit<
  TimeslotFormValues,
  "ruleList"
> {
  rulesById: Record<string, T>;
}

interface TimeslotRuleCountSelectProps {
  ruleId: string;
}

export function TimeslotRuleCountSelect({
  ruleId,
}: TimeslotRuleCountSelectProps): JSX.Element {
  const { watch, setValue } =
    useFormContext<FormValues<TimeslotRandomRule | TimeslotSequentialRule>>();

  const formValues = watch(`rulesById.${ruleId}`);
  const playlistId = formValues?.fast_channel_playlist_uuid;

  const elements = formValues?.elements;

  useEffect(() => {
    if (elements === 1) {
      setValue(`rulesById.${ruleId}.adbreak_uuid`, null);
    }
  }, [elements, setValue, ruleId]);

  const { channelId } = useParams<{ channelId: string }>();

  const getPlaylistListQuery = FastAPI.generateQuery({
    fn: FastAPI.getPlaylistList,
    params: [channelId],
  });

  const { data: playlistList } = useQuery({
    ...getPlaylistListQuery,
  });

  const generateCountOptions = (): { value: number; label: number }[] => {
    if (!playlistId) return [];
    const playlistData = playlistList?.data.find(
      (playlist) => playlist.uuid === playlistId,
    );
    if (!playlistData) return [];

    return Array.from({ length: playlistData.total_items }).map((_, i) => ({
      label: i + 1,
      value: i + 1,
    }));
  };

  return (
    <TimeslotRuleItemSelect
      isRequired
      defaultValue={{ value: 1, label: 1 }}
      fieldName={`rulesById.${ruleId}.elements`}
      isReadOnly={!playlistId}
      options={generateCountOptions()}
      placeholder="..."
    />
  );
}

interface TimeslotRuleIncrementSelectProps {
  ruleId: string;
}

export function TimeslotRuleIncrementSelect({
  ruleId,
}: TimeslotRuleIncrementSelectProps): JSX.Element {
  const { watch } = useFormContext<FormValues<TimeslotSequentialRule>>();

  const formValues = watch(`rulesById.${ruleId}`);
  const playlistId = formValues?.fast_channel_playlist_uuid;
  const elements = formValues?.elements;

  const generateIncrementOptions = (): { value: number; label: number }[] => {
    if (!elements) return [];

    return Array.from({ length: Number(elements) + 1 }).map((_, i) => ({
      label: 1 - i,
      value: 1 - i,
    }));
  };

  return (
    <TimeslotRuleItemSelect
      isRequired
      defaultValue={{ value: 1, label: 1 }}
      fieldName={`rulesById.${ruleId}.sequential_delta`}
      isReadOnly={!playlistId}
      options={generateIncrementOptions()}
      placeholder="..."
    />
  );
}

interface TimeslotRuleStartIndexSelectProps {
  ruleId: string;
}

export function TimeslotRuleStartIndexSelect({
  ruleId,
}: TimeslotRuleStartIndexSelectProps): JSX.Element {
  const { watch } = useFormContext<FormValues<TimeslotSequentialRule>>();
  const { t } = useTranslation("editor-fast-channel");

  const formValues = watch(`rulesById.${ruleId}`);
  const playlistId = formValues?.fast_channel_playlist_uuid;

  const { channelId } = useParams<{ channelId: string }>();

  const getPlaylistQuery = FastAPI.generateQuery({
    fn: FastAPI.getPlaylist,
    params: [{ channelId, playlistId }],
  });

  const { data: selectedPlaylistData, isFetching: isLoadingPlaylist } =
    useQuery({
      ...getPlaylistQuery,
      enabled: !!playlistId,
    });

  const generateStartIndexOptions = (): { value: number; label: string }[] => {
    if (!selectedPlaylistData) return [];

    return selectedPlaylistData.items
      .map((playlistMedia) => ({
        label: playlistMedia.label,
        value: playlistMedia.rank,
      }))
      .sort((a, b) => a.value - b.value);
  };

  return (
    <TimeslotRuleItemSelect
      isRequired
      fieldName={`rulesById.${ruleId}.sequential_start_index`}
      isLoading={isLoadingPlaylist}
      isReadOnly={!playlistId}
      options={generateStartIndexOptions()}
      placeholder={!playlistId ? t("selectPlaylistFirst") : undefined}
    />
  );
}
